import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Bank, ArrowsClockwise, MagnifyingGlass, Scales, Warning, CheckCircle, PencilSimple, ArrowUUpLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { DepositarChequeDialog, EditarChequeDialog } from "@/components/ChequesRecibidosDialogs";

const fmtMoney = (n, m = "ARS") =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: m, minimumFractionDigits: 2 }).format(n || 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("es-AR") : "-");

const estadoBadge = {
  aceptado: { label: "Aceptado", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  endosado: { label: "Endosado", className: "bg-blue-50 text-blue-700 border-blue-200" },
  depositado: { label: "Depositado", className: "bg-neutral-100 text-neutral-700 border-neutral-300" },
};

export default function ChequesPage() {
  const [cheques, setCheques] = useState([]);
  const [stats, setStats] = useState({ aceptado: { count: 0, total: 0 }, endosado: { count: 0, total: 0 }, depositado: { count: 0, total: 0 } });
  const [conc, setConc] = useState(null);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [depositar, setDepositar] = useState(null);
  const [editar, setEditar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterEstado, setFilterEstado] = useState("__all__");
  const [filterQ, setFilterQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterEstado !== "__all__") params.estado = filterEstado;
      const [{ data: chs }, { data: st }, concRes] = await Promise.all([
        api.get("/cheques", { params }),
        api.get("/cheques/stats"),
        api.get("/cheques/conciliacion").catch(() => ({ data: null })),
      ]);
      setCheques(chs || []);
      setStats(st || stats);
      setConc(concRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterEstado]);

  const crearFichas = async () => {
    try {
      const { data } = await api.post("/cheques/backfill-desde-cobros");
      toast.success(`${data.creados} fichas de cheque creadas (completá nº y banco con el lápiz)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const revertirDeposito = async (c) => {
    if (!window.confirm(`¿Revertir el depósito del cheque ${c.numero}? Vuelve a cartera y se anula la transferencia a ${c.depositado_caja_nombre}.`)) return;
    try {
      await api.post(`/cheques/${c.id}/revertir-deposito`);
      toast.success("Depósito revertido");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const difOk = conc && Math.abs(conc.diferencia || 0) < 0.01;

  const filtered = useMemo(() => {
    if (!filterQ) return cheques;
    const q = filterQ.toLowerCase();
    return cheques.filter((c) =>
      (c.numero || "").toLowerCase().includes(q) ||
      (c.banco_nombre || "").toLowerCase().includes(q) ||
      (c.cliente_nombre || "").toLowerCase().includes(q) ||
      (c.recibido_por || "").toLowerCase().includes(q) ||
      (c.endosado_proveedor_nombre || "").toLowerCase().includes(q)
    );
  }, [cheques, filterQ]);

  return (
    <div className="space-y-6" data-testid="cheques-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading flex items-center gap-2">
            <Bank size={28} weight="duotone" className="text-[#921A0E]" />
            Cheques Recibidos
          </h1>
          <p className="text-sm text-[#666] font-mono mt-1">
            Gestión de cheques de terceros en cartera y su endoso a proveedores.
          </p>
        </div>
        <Button onClick={load} variant="outline" size="sm" data-testid="refresh-cheques"
                className="rounded-sm">
          <ArrowsClockwise size={13} weight="bold" className="mr-1.5" />
          Actualizar
        </Button>
      </div>

      {/* Conciliación caja vs cartera */}
      {conc && (
        <div className={`border rounded-sm p-4 ${difOk ? "border-emerald-200 bg-emerald-50/40" : "border-amber-300 bg-amber-50/50"}`} data-testid="cheques-conciliacion">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-2">
              <Scales size={18} weight="bold" className={difOk ? "text-emerald-700" : "text-amber-700"} />
              <span className="text-[11px] uppercase tracking-widest font-mono text-[#666]">Caja "{conc.caja_nombre}" vs. cartera</span>
            </div>
            <div className="font-mono text-[13px]">Saldo caja <b data-testid="conc-saldo-caja">{fmtMoney(conc.saldo_caja)}</b></div>
            <div className="font-mono text-[13px]">En cartera <b data-testid="conc-cartera">{fmtMoney(conc.total_cartera)}</b></div>
            <div className={`font-mono text-[13px] font-semibold inline-flex items-center gap-1 ${difOk ? "text-emerald-700" : "text-amber-800"}`} data-testid="conc-diferencia">
              {difOk ? <CheckCircle size={14} weight="fill" /> : <Warning size={14} weight="fill" />}
              {difOk ? "Coinciden" : `Diferencia ${fmtMoney(conc.diferencia)}`}
            </div>
            <div className="ml-auto flex items-center gap-2">
              {conc.cobros_sin_cheque?.length > 0 && (
                <Button size="sm" onClick={crearFichas} className="rounded-sm h-8 text-[12px] bg-[#6B1F24] hover:bg-[#571519]" data-testid="crear-fichas-btn">
                  Crear fichas faltantes ({conc.cobros_sin_cheque.length})
                </Button>
              )}
              {!difOk && (
                <Button size="sm" variant="outline" onClick={() => setDetalleOpen((v) => !v)} className="rounded-sm h-8 text-[12px]" data-testid="conc-detalle-btn">
                  {detalleOpen ? "Ocultar detalle" : "Ver detalle"}
                </Button>
              )}
            </div>
          </div>
          {detalleOpen && !difOk && (
            <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3 text-[12px]" data-testid="conc-detalle">
              <div className="bg-white border rounded-sm p-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#666] mb-1">
                  Cobros en la caja sin ficha de cheque · {fmtMoney(conc.cobros_sin_cheque_total)}
                </div>
                {conc.cobros_sin_cheque.length === 0 ? <div className="text-neutral-400">Ninguno</div> : conc.cobros_sin_cheque.map((c) => (
                  <div key={c.id} className="flex justify-between gap-2 border-t py-1 first:border-0">
                    <span className="truncate">{fmtDate(c.fecha)} · {c.contraparte_nombre} <span className="text-neutral-400">{c.concepto}</span></span>
                    <span className="font-mono shrink-0">{fmtMoney(c.monto)}</span>
                  </div>
                ))}
              </div>
              <div className="bg-white border rounded-sm p-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#666] mb-1">
                  Movimientos de la caja sin cheque asociado (transferencias, manuales, Excel) · {fmtMoney(conc.movs_sin_cheque_total)}
                </div>
                {conc.movs_sin_cheque.length === 0 ? <div className="text-neutral-400">Ninguno</div> : conc.movs_sin_cheque.map((m) => (
                  <div key={m.id} className="flex justify-between gap-2 border-t py-1 first:border-0">
                    <span className="truncate">{fmtDate(m.fecha)} · {m.concepto} <span className="text-neutral-400">({m.referencia_tipo || "manual"})</span></span>
                    <span className={`font-mono shrink-0 ${m.delta >= 0 ? "text-emerald-700" : "text-red-700"}`}>{m.delta >= 0 ? "+" : ""}{fmtMoney(m.delta)}</span>
                  </div>
                ))}
                {conc.cheques_sin_cobro?.length > 0 && (
                  <div className="mt-2 text-amber-800">{conc.cheques_sin_cobro.length} cheque(s) cuyo cobro ya no existe: {conc.cheques_sin_cobro.map((c) => c.numero).join(", ")}</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="stat-aceptado" className="border-emerald-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-widest text-emerald-700 font-mono">
              En cartera (aceptados)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-heading text-emerald-800">
              {fmtMoney(stats.aceptado?.total || 0)}
            </div>
            <div className="text-[11px] font-mono text-[#666] mt-1">
              {stats.aceptado?.count || 0} cheques
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-endosado" className="border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-widest text-blue-700 font-mono">
              Endosados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-heading text-blue-800">
              {fmtMoney(stats.endosado?.total || 0)}
            </div>
            <div className="text-[11px] font-mono text-[#666] mt-1">
              {stats.endosado?.count || 0} cheques
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-depositado" className="border-neutral-300">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-widest text-neutral-600 font-mono">
              Depositados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-heading text-neutral-800">
              {fmtMoney(stats.depositado?.total || 0)}
            </div>
            <div className="text-[11px] font-mono text-[#666] mt-1">
              {stats.depositado?.count || 0} cheques
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5E5E5] bg-[#F9F6F6]">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-widest text-[#6B1F24] font-mono">
              Total histórico
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-heading text-[#6B1F24]">
              {fmtMoney((stats.aceptado?.total || 0) + (stats.endosado?.total || 0) + (stats.depositado?.total || 0))}
            </div>
            <div className="text-[11px] font-mono text-[#666] mt-1">
              {(stats.aceptado?.count || 0) + (stats.endosado?.count || 0) + (stats.depositado?.count || 0)} cheques recibidos
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 border border-[#E5E5E5] rounded-sm p-3 bg-white">
        <div className="flex items-center gap-2">
          <MagnifyingGlass size={14} className="text-[#999]" />
          <Input
            data-testid="filter-cheques-search"
            placeholder="Buscar por nº, banco, cliente o proveedor…"
            value={filterQ}
            onChange={(e) => setFilterQ(e.target.value)}
            className="rounded-sm h-8 text-[12px] w-72"
          />
        </div>
        <Select value={filterEstado} onValueChange={setFilterEstado}>
          <SelectTrigger className="h-8 w-40 text-[12px] rounded-sm" data-testid="filter-cheques-estado">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los estados</SelectItem>
            <SelectItem value="aceptado">Aceptado</SelectItem>
            <SelectItem value="endosado">Endosado</SelectItem>
            <SelectItem value="depositado">Depositado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabla */}
      <div className="border border-[#E5E5E5] rounded-sm overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px] font-mono">
            <thead className="bg-[#F9F6F6] text-[10px] uppercase tracking-widest text-[#666]">
              <tr>
                <th className="px-3 py-2 text-left">Nº Cheque</th>
                <th className="px-3 py-2 text-left">Banco</th>
                <th className="px-3 py-2 text-left">Recibido de</th>
                <th className="px-3 py-2 text-left">Cliente (cta cte)</th>
                <th className="px-3 py-2 text-left">F. Emisión</th>
                <th className="px-3 py-2 text-left">F. Cobro</th>
                <th className="px-3 py-2 text-right">Monto</th>
                <th className="px-3 py-2 text-left">Estado</th>
                <th className="px-3 py-2 text-left">Endosado a / Depositado en</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="p-4 text-center text-[#999]">Cargando…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={10} className="p-8 text-center text-[#999]">
                  Sin cheques que coincidan con los filtros.
                </td></tr>
              )}
              {filtered.map((c) => {
                const badge = estadoBadge[c.estado] || { label: c.estado, className: "" };
                return (
                  <tr key={c.id} data-testid={`cheque-row-${c.id}`}
                      className="border-t border-[#F0F0F0] hover:bg-[#FAFAFA]">
                    <td className="px-3 py-2 font-semibold">
                      {c.numero}
                      {c.datos_pendientes && (
                        <span className="ml-1 text-[9px] uppercase bg-amber-100 text-amber-800 border border-amber-200 rounded-sm px-1 py-0.5" data-testid={`pendiente-datos-${c.id}`}>datos pendientes</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[11px]">
                      <span className="text-[#999] mr-1">{c.banco_code}</span>
                      {c.banco_nombre}
                    </td>
                    <td className="px-3 py-2 font-semibold">
                      {c.recibido_por || c.cliente_nombre || "-"}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-[#666]">{c.cliente_nombre}</td>
                    <td className="px-3 py-2">{fmtDate(c.fecha_emision)}</td>
                    <td className="px-3 py-2">{fmtDate(c.fecha_cobro)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmtMoney(c.monto, c.moneda)}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`rounded-sm text-[10px] uppercase tracking-widest ${badge.className}`}>
                        {badge.label}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-[11px] text-[#666]">
                      {c.endosado_proveedor_nombre ? (
                        <div>
                          <div className="font-semibold text-[#333]">{c.endosado_proveedor_nombre}</div>
                          <div className="text-[10px]">{fmtDate(c.endosado_at)}</div>
                        </div>
                      ) : c.depositado_caja_nombre ? (
                        <div>
                          <div className="font-semibold text-[#333]"><Bank size={11} className="inline mr-1" />{c.depositado_caja_nombre}</div>
                          <div className="text-[10px]">{fmtDate(c.depositado_fecha)}</div>
                        </div>
                      ) : "-"}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button onClick={() => setEditar(c)} data-testid={`editar-cheque-${c.id}`} title="Editar datos"
                        className="p-1.5 rounded-sm text-[#666] hover:text-[#6B1F24] hover:bg-[#F5F1E9]">
                        <PencilSimple size={13} />
                      </button>
                      {c.estado === "aceptado" && (
                        <Button size="sm" onClick={() => setDepositar(c)} data-testid={`depositar-cheque-${c.id}`}
                          className="rounded-sm h-7 text-[11px] ml-1 bg-[#6B1F24] hover:bg-[#571519]">
                          <Bank size={12} className="mr-1" /> Depositar
                        </Button>
                      )}
                      {c.estado === "depositado" && (
                        <button onClick={() => revertirDeposito(c)} data-testid={`revertir-deposito-${c.id}`} title="Revertir depósito"
                          className="p-1.5 rounded-sm text-[#666] hover:text-red-700 hover:bg-red-50 ml-1">
                          <ArrowUUpLeft size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <DepositarChequeDialog cheque={depositar} onClose={() => setDepositar(null)} onDone={load} />
      <EditarChequeDialog cheque={editar} onClose={() => setEditar(null)} onDone={load} />
    </div>
  );
}
